import { useEffect, useState } from 'react';
import styles from './LifeEventsCalculator.module.css';

const STOPS = [
    { m: 3, pos: 0, label: '3 months' },
    { m: 6, pos: 14, label: '6 months' },
    { m: 9, pos: 29, label: '9 months' },
    { m: 12, pos: 43, label: '1 year' },
    { m: 24, pos: 100, label: '2 years' },
];

// Total-body-weight loss (as a fraction) reached by each timeline stop, per medication.
// Anchored on the 1-year and 2-year averages; sub-year stops ramp up to the 1-year figure.
const MEDS = [
    {
        name: 'Semaglutide',
        sub: 'avg ~15% loss',
        lossByMonth: { 3: 0.045, 6: 0.0825, 9: 0.12, 12: 0.15, 24: 0.17 },
    },
    {
        name: 'Tirzepatide',
        sub: 'avg ~21% loss',
        lossByMonth: { 3: 0.063, 6: 0.1155, 9: 0.168, 12: 0.21, 24: 0.23 },
    },
];

const EVENT_TILES = [
    {
        rate: 12,
        label: 'Full moons',
        sub: 'Look up — there it is again',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a9 9 0 1 0 9 9 7 7 0 0 1-9-9z"></path></svg>,
    },
    {
        rate: 1,
        label: 'Christmas celebrations',
        sub: 'Around the tree, feeling like yourself',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 6 11h3.5L5 18h14l-4.5-7H18z"></path><line x1="12" y1="18" x2="12" y2="22"></line></svg>,
    },
    {
        rate: 2,
        label: 'Weddings',
        sub: 'On the dance floor, not the sidelines',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="16" r="6"></circle><path d="M8 3h8l2 3-6 4-6-4z"></path><line x1="8" y1="3" x2="12" y2="6"></line><line x1="16" y1="3" x2="12" y2="6"></line></svg>,
    },
    {
        rate: 8,
        label: 'Family birthday parties',
        sub: 'Present for every candle',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21h16v-7H4z"></path><path d="M4 14c1.5 0 1.5-2 3-2s1.5 2 3 2 1.5-2 3-2 1.5 2 3 2 1.5-2 3-2"></path><line x1="12" y1="4" x2="12" y2="8"></line><circle cx="12" cy="3" r="1"></circle></svg>,
    },
    {
        rate: 52,
        label: 'Sunday brunches',
        sub: 'Slow mornings with the people you love',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 8h13v5a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4z"></path><path d="M17 9h2a2 2 0 0 1 0 4h-2"></path><line x1="7" y1="2" x2="7" y2="5"></line><line x1="11" y1="2" x2="11" y2="5"></line></svg>,
    },
    {
        rate: 3,
        label: 'Vacation trips',
        sub: 'Keeping up, not sitting out',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2 16 11l3.5-3.5a2.1 2.1 0 0 0-3-3L13 8 4.8 6.2a1 1 0 0 0-1 .3l-.9.9 6 3.4-2.4 2.4H3.9l-.9.9 3.3 1.8L8.1 19l.9-.9v-2.6l2.4-2.4 3.4 6 .9-.9z"></path></svg>,
    },
];

const nearestStop = (pos) => STOPS.reduce((a, b) => (Math.abs(b.pos - pos) < Math.abs(a.pos - pos) ? b : a));

function loadSaved() {
    try {
        const s = JSON.parse(localStorage.getItem('tb2-calc'));
        if (!s) return null;
        return s;
    } catch (e) {
        return null;
    }
}

export default function LifeEventsCalculator() {
    const saved = loadSaved();

    const [lbs, setLbs] = useState(() => (saved && saved.w >= 120 && saved.w <= 300 ? saved.w : 240));
    const [medName, setMedName] = useState(() => (saved && MEDS.some((m) => m.name === saved.med) ? saved.med : MEDS[0].name));
    const [selMonth, setSelMonth] = useState(() => (saved && STOPS.some((s) => s.m === saved.m) ? saved.m : 12));

    const med = MEDS.find((m) => m.name === medName) || MEDS[0];
    const stop = STOPS.find((s) => s.m === selMonth) || STOPS[0];

    const lbsLost = lbs * (med.lossByMonth[stop.m] ?? 0);
    const lost = Math.round(lbsLost);
    const projW = lbs - lost;
    const yearsGained = lbsLost / 6;

    const weightFill = ((lbs - 120) / 180) * 100;
    const lbsDisplay = lbs >= 300 ? '300+' : lbs;
    const stopDeterminer = /^1 /.test(stop.label) ? 'That' : 'Those';

    useEffect(() => {
        try {
            localStorage.setItem('tb2-calc', JSON.stringify({ w: lbs, med: medName, m: selMonth }));
        } catch (e) { /* ignore */ }
    }, [lbs, medName, selMonth]);

    return (
        <section className={styles.calcSec} id="calculator" data-screen-label="Life Events Calculator">
            <div className={styles.calcHead}>
                <div className={styles.eyebrow}>The Life Events Calculator</div>
                <h2>Weight isn't just pounds. It's moments.</h2>
                <p>Choose your medication and current weight, then see where the months ahead could take you — and the everyday moments that fill them.</p>
            </div>
            <div className={styles.calcCard}>
                <div className={styles.calcControls}>
                    <div>
                        <div className={styles.calcSliderRow}>
                            <div className={styles.calcSliderLabel}>My current weight</div>
                            <div className={styles.calcLbs}><span>{lbsDisplay}</span> <span>lbs</span></div>
                        </div>
                        <input
                            type="range"
                            className={styles.calcRange}
                            min="120"
                            max="300"
                            step="5"
                            value={lbs}
                            onChange={(e) => setLbs(+e.target.value)}
                            style={{ '--fill': `${weightFill}%` }}
                            aria-label="Current weight"
                        />
                        <div className={styles.calcRangeEnds}><span>120 lbs</span><span>300+ lbs</span></div>
                    </div>
                    <div className={styles.medBlock}>
                        <div className={styles.medLabel}>My medication</div>
                        <div className={styles.medPills}>
                            {MEDS.map((medOption) => {
                                const active = medOption.name === medName;
                                return (
                                    <button
                                        key={medOption.name}
                                        className={`${styles.medPill} ${active ? styles.active : ''}`}
                                        type="button"
                                        onClick={() => setMedName(medOption.name)}
                                    >
                                        <div className={styles.mn}>{medOption.name}</div>
                                        <div className={styles.ms}>{medOption.sub}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <p className={styles.calcHeadline}>In <b>{stop.label}</b> on <b>{medName}</b>, you could reach <b className={styles.hl}>{projW} lbs</b> — about <b>{lost} lbs</b> lighter.</p>
                <div className={styles.tlLabel}>Look ahead to&hellip;</div>
                <div className={styles.timelineTrack}>
                    <input
                        type="range"
                        className={`${styles.calcRange} ${styles.tlRange}`}
                        min="0"
                        max="100"
                        step="1"
                        value={stop.pos}
                        onChange={(e) => setSelMonth(nearestStop(+e.target.value).m)}
                        style={{ '--fill': `${stop.pos}%` }}
                        aria-label="Time horizon"
                    />
                    <div className={styles.tlMarks}>
                        {STOPS.map((s, i) => {
                            const active = s.m === selMonth;
                            const edge = i === 0 ? styles.tlMarkStart : i === STOPS.length - 1 ? styles.tlMarkEnd : '';
                            const shortLabel = { 3: '3 mo', 6: '6 mo', 9: '9 mo', 12: '1 yr', 24: '2 yr' }[s.m];
                            return (
                                <button
                                    key={s.m}
                                    className={`${styles.tlMark} ${edge} ${active ? styles.active : ''}`}
                                    type="button"
                                    style={{ left: `${s.pos}%` }}
                                    onClick={() => setSelMonth(s.m)}
                                >
                                    <span className={styles.tlDot}></span>
                                    <span className={styles.tlMl}>{shortLabel}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className={styles.eventsLabel}>{stopDeterminer} <span>{stop.label}</span>, in moments gained back</div>
                <div className={styles.eventsGrid}>
                    {EVENT_TILES.map((tile) => {
                        const n = Math.max(1, Math.round(tile.rate * yearsGained));
                        return (
                            <div className={styles.eventTile} key={tile.label}>
                                <div className={styles.ico}>{tile.icon}</div>
                                <div className={styles.n}>{n.toLocaleString()}</div>
                                <div className={styles.l}>{tile.label}</div>
                                <div className={styles.s}>{tile.sub}</div>
                            </div>
                        );
                    })}
                </div>
                <p className={styles.calcNote}>Projections are estimates based on average total-body-weight reductions reported in clinical trials of GLP-1 medications (roughly 15% for semaglutide, 21% for tirzepatide) and will vary with dose, adherence, and individual response. These estimates assume common obesity-related comorbidities such as Type&nbsp;2 diabetes or hypertension, where sustained weight loss has the greatest measurable benefit. This is general guidance, not medical advice — always follow your licensed clinician's instructions.</p>
            </div>
        </section>
    );
}
